import React from 'react';
import { createPortal } from 'react-dom';
import { UserWithRole, Role } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface RoleChangeModalProps {
  user: UserWithRole;
  roles: Role[];
  newRoleId: number;
  reason: string;
  updating: boolean;
  onRoleIdChange: (roleId: number) => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const RoleChangeModal: React.FC<RoleChangeModalProps> = ({
  user,
  roles,
  newRoleId,
  reason,
  updating,
  onRoleIdChange,
  onReasonChange,
  onConfirm,
  onCancel
}) => {
  const { t } = usePageTranslation('panel', 'UserRolesManagement');

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="rounded-xl border border-[#243052]/90 bg-[#0a1128] p-6 shadow-2xl max-w-md w-full">
        <h3 className="text-lg font-bold text-white mb-5">
          {t('modal.changeRole.title')} — {user.username}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              {t('modal.changeRole.newRole')}
            </label>
            <select
              value={newRoleId}
              onChange={(e) => onRoleIdChange(Number(e.target.value))}
              className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#3d4fdb]/60"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name} - {role.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">
              {t('modal.changeRole.reason')} ({t('modal.changeRole.optional')})
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={t('modal.changeRole.reasonPlaceholder')}
              rows={3}
              className="w-full rounded-lg border border-[#2a3a62] bg-[#0a132b] px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#3d4fdb]/60 resize-none"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button
              onClick={onConfirm}
              disabled={updating || newRoleId === user.roles_id}
              className="flex-1 rounded-lg border border-purple-500/40 bg-purple-500/15 py-2.5 text-sm font-semibold text-purple-200 hover:bg-purple-500/25 disabled:opacity-40 transition-colors"
            >
              {updating ? t('modal.changeRole.updating') : t('modal.changeRole.confirm')}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 rounded-lg border border-white/15 bg-black/30 py-2.5 text-sm font-semibold text-slate-200 hover:bg-black/45 transition-colors"
            >
              {t('modal.changeRole.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RoleChangeModal;
